import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Search, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import apiClient from '../../services/apiClient';

// Jira user list changes rarely; cache across modal opens so reopening is instant.
const USERS_CACHE = { data: null, ts: 0 };
const CACHE_TTL = 5 * 60 * 1000; // 5 min

// /user/assignable/search may omit account_type; only drop users who are
// explicitly inactive or explicitly non-atlassian. Keep everyone else.
const normalizeUsers = (users) =>
  users.filter((u) => {
    const accountType = u.account_type ?? u.accountType;
    return u.active !== false && (!accountType || accountType === 'atlassian');
  });

export default function AddTeamMemberModal({  onClose,
  onSubmit,
  loading = false,
  existingAccountIds = [],
}) {
  const [jiraUsers, setJiraUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);

  const fetchUsers = useCallback(async (signal, force = false) => {
    if (!force && USERS_CACHE.data && Date.now() - USERS_CACHE.ts < CACHE_TTL) {
      setJiraUsers(USERS_CACHE.data); // instant, no spinner
      return;
    }
    setLoadingUsers(true);
    try {
      const res = await apiClient.get('/jira/all_users', { signal });
      const cleaned = normalizeUsers(res?.data ?? []);
      USERS_CACHE.data = cleaned;
      USERS_CACHE.ts = Date.now();
      setJiraUsers(cleaned);
    } catch (err) {
      if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError' || signal?.aborted) return;
      toast.error('Failed to load Jira users');
      setJiraUsers([]);
    } finally {
      if (!signal?.aborted) setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchUsers(controller.signal);
    return () => controller.abort();
  }, [fetchUsers]);
  
  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return undefined;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  // Escape handling
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== 'Escape' || loading) return;
      if (showDropdown) setShowDropdown(false);
      else onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [loading, onClose, showDropdown]);

  const existingSet = useMemo(() => new Set(existingAccountIds), [existingAccountIds]);

  const filteredUsers = useMemo(
    () =>
      jiraUsers.filter(
        (u) =>
          !search ||
          (u.display_name || '').toLowerCase().includes(search.toLowerCase()) ||
          (u.email || '').toLowerCase().includes(search.toLowerCase())
      ),
    [jiraUsers, search]
  );

  const handleSelectUser = (user) => {
    if (existingSet.has(user.account_id)) return;
    setSelectedUser(user);
    setSearch(user.display_name || '');
    setShowDropdown(false);
    setError(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedUser) {
      setError('Please select a Jira user');
      return;
    }
    onSubmit(selectedUser);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-team-member-title"
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4"
      onClick={() => !loading && onClose()}
    >
      <div
        className="rounded-xl w-full max-w-lg bg-white dark:bg-slate-800 shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 max-h-[90vh] overflow-y-auto">
          <h3
            id="add-team-member-title"
            className="text-xl font-semibold text-slate-900 dark:text-white mb-5"
          >
            Add team member
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="z-20" ref={dropdownRef}>
              <label
                htmlFor="team-member-search"
                className="block mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                Jira user *
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-slate-400" />
                <input
                  id="team-member-search"
                  type="text"
                  role="combobox"
                  aria-expanded={showDropdown}
                  aria-controls="team-member-listbox"
                  aria-autocomplete="list"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setSelectedUser(null);
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  disabled={loadingUsers}
                  placeholder={loadingUsers ? 'Loading users...' : 'Search by name or email...'}
                  className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100 dark:disabled:bg-slate-700 disabled:cursor-not-allowed ${
                    error
                      ? 'border-red-500'
                      : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white'
                  }`}
                />
              </div>

              {showDropdown && !loadingUsers && (
                <ul
                  id="team-member-listbox"
                  role="listbox"
                  aria-label="Jira users"
                  className="z-50 mt-1 w-full max-h-56 overflow-y-auto overscroll-contain rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg py-1"
                >
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((u) => {
                      const alreadyAdded = existingSet.has(u.account_id);
                      return (
                        <li
                          key={u.account_id}
                          role="option"
                          aria-selected={selectedUser?.account_id === u.account_id}
                        >
                          <button
                            type="button"
                            onClick={() => handleSelectUser(u)}
                            disabled={alreadyAdded}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <img src={u.avatar_url} alt="" className="size-7 rounded-full shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-slate-900 dark:text-white truncate">
                                {u.display_name}
                              </p>
                              {u.email && (
                                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                  {u.email}
                                </p>
                              )}
                            </div>
                            {alreadyAdded && (
                              <span className="text-xs text-slate-400 shrink-0">Added</span>
                            )}
                          </button>
                        </li>
                      );
                    })
                  ) : (
                    <li className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
                      No users found
                    </li>
                  )}
                </ul>
              )}
              {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!selectedUser || loading}
                className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <RefreshCw className="size-4 animate-spin mr-2" />
                    Adding...
                  </>
                ) : (
                  'Add'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}